import * as React from 'react';
import { useState } from 'react';
import { Popper, Button, Box, InputBase, Checkbox, ButtonGroup, Autocomplete, ClickAwayListener, FormControlLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Filter } from 'src/types/filter';
import { FilterOption } from 'src/types/filterOption';
import '../../theme/partials/DropdownMenu.css';

interface PopperProps {
  anchorEl: any;
  disablePortal: boolean;
  open: boolean;
}

interface Props {
  emitFilterData: (data: FilterOption[]) => void;
  label: string;
  filter: Filter;
  onAutocompleteChange?: (e: any, val: string) => void;
  selectedOptions?: FilterOption[];
}

const PopperComponent: React.FC<PopperProps> = (props) => {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <div {...other} />;
};

const DropdownFilterMenu: React.FC<Props> = ({ emitFilterData, label, filter, onAutocompleteChange, selectedOptions = [] }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [assignAll, setAssignAll] = useState(false);
  const options: FilterOption[] = filter.options;
  const [inputValue, setInputValue] = useState('');

  // Open popper
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Close popper
  const handleClose = () => {
    setAnchorEl(null);
    setInputValue('');
  };

  // Clear all selections
  const handleClearAll = () => {
    setAssignAll(false);
    emitFilterData([]);
  };

  // Select/Deselect all
  const handleAssignAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAssignAll(checked);
    emitFilterData(checked ? options : []);
  };

  const openDropdown = Boolean(anchorEl);
  const id = openDropdown ? 'filter-menu-dropdown' : undefined;

  return (
    <Box className="dropdown-filter-menu">
      <Box>
        {selectedOptions.length > 0 ? (
          // If there are selected items, show a contained button + a clear button
          <ButtonGroup disableRipple className="filter-btn-group">
            <Button id={filter.field} onClick={handleClick} variant="contained" endIcon={<ArrowDropDownIcon />} size="small">
              <Box sx={{ pr: '3px' }}>{label}:</Box>
              {selectedOptions[0].name}
              {selectedOptions.length > 1 && <Box sx={{ pl: '3px' }}>+{selectedOptions.length - 1}</Box>}
            </Button>
            <Button onClick={handleClearAll} variant="outlined" size="small">
              <CloseIcon />
            </Button>
          </ButtonGroup>
        ) : (
          // If no selection, just show a text button to open the popper
          <ButtonGroup disableRipple className="filter-btn-group-link">
            <Button id={filter.field} onClick={handleClick} variant="text" endIcon={<ArrowDropDownIcon />} size="small">
              {label}
            </Button>
          </ButtonGroup>
        )}
      </Box>
      <Box sx={{ position: 'relative' }}>
        <Popper id={id} open={openDropdown} anchorEl={anchorEl} placement="bottom-start" className="filter-box">
          <ClickAwayListener onClickAway={handleClose}>
            <Box>
              <Autocomplete
                open
                multiple
                value={selectedOptions}
                inputValue={inputValue}
                onChange={(event, newValue) => {
                  emitFilterData(newValue as FilterOption[]);
                }}
                disableCloseOnSelect
                PopperComponent={PopperComponent}
                limitTags={1}
                isOptionEqualToValue={(option, value) => {
                  if (option.value != null && value.value != null) {
                    return option.value === value.value;
                  }
                  return option.name === value.name;
                }}
                getOptionLabel={(option) => option.name}
                options={options}
                renderOption={(props, option, { selected }) => (
                  <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox checked={selected} size="small" sx={{ p: '3px' }} />
                    <Box sx={{ flexGrow: 1 }}>{option.name}</Box>
                  </li>
                )}
                renderInput={(params) => <InputBase ref={params.InputProps.ref} inputProps={params.inputProps} autoFocus placeholder={filter.title} className="styled-input" />}
                onInputChange={(e, val, reason) => {
                  if (reason === 'input') {
                    setInputValue(val);
                    onAutocompleteChange?.(e, val);
                  }
                }}
              />
              <Box sx={{ px: '11px' }} className="filter-footer-achored">
                <FormControlLabel control={<Checkbox size="small" checked={assignAll} />} onChange={handleAssignAll} label="Select All" />
              </Box>
            </Box>
          </ClickAwayListener>
        </Popper>
      </Box>
    </Box>
  );
};

export default DropdownFilterMenu;
